export { buildCampaignRunPrompt } from "@runcheetah/shared";

export const POST_TYPE_LABELS = {
  feed_portrait: "Instagram Feed Portrait",
  feed_square: "Instagram Feed Square",
  carousel_portrait: "Instagram Carousel Portrait",
  carousel_square: "Instagram Carousel Square",
  story: "Instagram Story",
  tiktok_portrait: "TikTok Portrait",
  tiktok_carousel_portrait: "TikTok Carousel Portrait",
} as const;

export const PLANNED_POST_STATUS_CONFIG: Record<string, { label: string; className: string }> = {
  draft: { label: "Planned", className: "border-slate-200 bg-slate-100 text-slate-700" },
  scheduled: { label: "Queued", className: "border-blue-200 bg-blue-50 text-blue-700" },
  generating: { label: "Generating", className: "border-amber-200 bg-amber-50 text-amber-700" },
  generated: { label: "Ready", className: "border-green-200 bg-green-50 text-green-700" },
  failed: { label: "Needs Attention", className: "border-red-200 bg-red-50 text-red-700" },
};

export const getPostTypeLabel = (postType: string): string => {
  return (
    POST_TYPE_LABELS[postType as keyof typeof POST_TYPE_LABELS] ?? postType.replaceAll("_", " ")
  );
};

export const getPlannedPostPrimaryActionState = (input: {
  postStatus: string;
  hasAttempt: boolean;
  hasCompleted: boolean;
  hasActiveRun: boolean;
}): { kind: "generate" | "status"; label: string } => {
  if (input.hasActiveRun || input.postStatus === "scheduled" || input.postStatus === "generating") {
    return { kind: "status", label: "Generation in progress" };
  }

  if (!input.hasAttempt || input.postStatus === "draft") {
    return { kind: "generate", label: "Generate First Attempt" };
  }

  if (input.postStatus === "failed") {
    return { kind: "generate", label: "Regenerate Planned Post" };
  }

  if (input.hasCompleted || input.postStatus === "generated") {
    return { kind: "generate", label: "Generate Another Attempt" };
  }

  return { kind: "generate", label: "Generate Planned Post" };
};

export const getPlannedPostGenerationPanelState = (input: {
  postStatus: string;
  hasAttempt: boolean;
  hasCompleted: boolean;
}) => {
  if (!input.hasAttempt || input.postStatus === "draft") {
    return {
      mode: "first_attempt" as const,
      title: "Generate first attempt",
      description:
        "Create the first creative for this planned post using the campaign strategy, objective, audience, format, and CTA already defined.",
      directionLabel: "Optional emphasis",
      directionPlaceholder:
        "Anything the first attempt should lean into? For example: founder credibility, faster product understanding, calmer composition.",
      ctaLabel: "Queue first attempt",
      nextStepTitle: "What happens next",
      nextStepDescription:
        "A new generation attempt is queued and this workspace will shift focus to it so you can review the creative as soon as it is ready.",
    };
  }

  if (input.postStatus === "failed" || !input.hasCompleted) {
    return {
      mode: "regenerate" as const,
      title: "Regenerate planned post",
      description:
        "Create a fresh attempt for this planned post. Use extra direction only when you want the next result to correct or noticeably change the previous attempt.",
      directionLabel: "What should change?",
      directionPlaceholder:
        "Describe what the next attempt should improve, avoid, or emphasize differently.",
      ctaLabel: "Queue regeneration",
      nextStepTitle: "What happens next",
      nextStepDescription:
        "The new attempt is added to this planned post's history so you can compare it against the previous output without losing context.",
    };
  }

  return {
    mode: "iterate" as const,
    title: "Generate another attempt",
    description:
      "Explore a new variation while keeping the same planned-post strategy. This is useful when the current creative works, but you want another option to compare.",
    directionLabel: "Optional variation direction",
    directionPlaceholder:
      "Describe the variation you want to explore, such as tone, composition, or emphasis.",
    ctaLabel: "Queue another attempt",
    nextStepTitle: "What happens next",
    nextStepDescription:
      "A new attempt is added alongside the existing one so you can review the latest version in context and keep the earlier creative as a reference.",
  };
};

export const getPlannedPostGenerationGuardrailMessage = (input: {
  brandStatus: string;
  hasSelectedPost: boolean;
  hasActiveRun: boolean;
}) => {
  if (!input.hasSelectedPost) {
    return "Choose a planned post before starting generation.";
  }

  if (input.brandStatus !== "scanned") {
    return `This brand is currently ${input.brandStatus}. Generation is enabled once scanning is complete.`;
  }

  if (input.hasActiveRun) {
    return "A generation attempt is already in progress for this planned post.";
  }

  return null;
};

export const canRetryFailedGeneration = (input: {
  brandStatus: string;
  taskStatus: string | null | undefined;
  hasActiveRun: boolean;
  hasRequest: boolean;
}) =>
  input.brandStatus === "scanned" &&
  input.taskStatus === "failed" &&
  !input.hasActiveRun &&
  input.hasRequest;

export const buildCampaignDetailPath = (brandId: string, campaignId: string) =>
  `/brands/${brandId}/campaigns/${campaignId}`;

export const buildPlannedPostDetailPath = (brandId: string, campaignId: string, postId: string) =>
  `/brands/${brandId}/campaigns/${campaignId}/planned-posts/${postId}`;
